import {
  MongoReturnId,
  MongoReturnItemId,
  MongoUserId,
} from "@redotech/redo-ids-mongo";
import * as z from "zod";

export const InventoryItemKindSchema = z.enum([
  "PRODUCT",
  "RETURN",
  "UNDECLARED",
  "UNKNOWN",
]);
export type InventoryItemKind = z.infer<typeof InventoryItemKindSchema>;

export const InventoryItemClassSchema = z.enum(["lot", "custom"]);
export type InventoryItemClass = z.infer<typeof InventoryItemClassSchema>;

export const InventoryItemGradeSchema = z.string();
export type InventoryItemGrade = z.infer<typeof InventoryItemGradeSchema>;

export const InventoryItemDispositionSchema = z.enum([
  "restock",
  "liquidate",
  "donate",
  "dispose",
  "return_to_vendor",
]);
export type InventoryItemDisposition = z.infer<
  typeof InventoryItemDispositionSchema
>;

const InventoryItemEventTypeSchema = z.enum([
  "restocked",
  "picked",
  "shipped",
  "committed",
  "released",
]);
export type InventoryItemEventType = z.infer<
  typeof InventoryItemEventTypeSchema
>;

// Derived from live inventory-level placement; not a stored column.
export const InventoryItemLifecycleStatus = {
  atWarehouse: "at_warehouse",
  shipped: "shipped",
} as const;
export type InventoryItemLifecycleStatus =
  (typeof InventoryItemLifecycleStatus)[keyof typeof InventoryItemLifecycleStatus];
export const InventoryItemLifecycleStatusSchema = z.enum([
  InventoryItemLifecycleStatus.atWarehouse,
  InventoryItemLifecycleStatus.shipped,
]);

export interface InventoryItemRecord {
  id: bigint;
  accountId: bigint;
  kind: InventoryItemKind;
  productId: bigint | null;
  returnId: string | null;
  returnItemId: string | null;
  locationId: bigint;
  storageLocationId: bigint | null;
  quantity: bigint;
  class: InventoryItemClass | null;
  grade: InventoryItemGrade | null;
  disposition: InventoryItemDisposition | null;
  lotNumber: string | null;
  expirationDate: Date | null;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
}

export interface InventoryItemWithAvailability extends InventoryItemRecord {
  available: bigint;
  storageLocationName: string | null;
  productTitle: string | null;
  productSku: string | null;
  productHsCode: string | null;
  productOriginCountry: string | null;
  productWeight: number | null;
  productWeightUnit: string | null;
  productPrice: number | null;
  productCurrency: string | null;
  productManufacturerId: bigint | null;
  productImageUrl: string | null;
}

export interface CreateInventoryItemInput {
  accountId: bigint;
  kind: InventoryItemKind;
  locationId: bigint;

  // Required for kind=PRODUCT
  productId?: bigint;

  // Required for kind=RETURN (not required for kind=UNKNOWN)
  returnId?: MongoReturnId;

  // Optional
  returnItemId?: MongoReturnItemId;
  storageLocationId?: bigint;
  quantity?: number;
  class?: InventoryItemClass;
  grade?: InventoryItemGrade;
  disposition?: InventoryItemDisposition;

  // Lot tracking
  lotNumber?: string;
  expirationDate?: Date;

  // For event tracking
  userId?: MongoUserId;
}

export interface DeleteInventoryItemInput {
  id: bigint;
  accountId: bigint;
}

export interface CreateInventoryItemEventInput {
  inventoryItemId: bigint;
  eventType: InventoryItemEventType;
  userId?: MongoUserId;
  payload?: Record<string, unknown>;
}
